import * as React from 'react';
import * as _ from 'lodash';
import {
  API,
  ContactCollection,
  Contact,
  ContentLoading,
  CustomerStatus,
  CustomerSummaryCollection,
  CustomerSummary,
  formatDecimal,
  Logging,
  Notification,
  NotificationData,
  ResponseError,
  StatusIndicator,
  stringToDate,
  UserInfo,
  PermissionGroup,
  UserContext,
  getEditPermission,
  getCurrentDateOnly,
} from '@vcd-ecm-ui/Common';
import { ColumnSwitch, GenericGrid, mergeDefaultAndCustomColumns } from '@vcd-ecm-ui/GenericGrid';
import { useDispatch } from 'react-redux';
import { VtxColumnProps, VtxInputSearch } from '@vertexinc/vtx-ui-react-component-library';
import GridRowData, { GridDataNativeType } from './GridRowData';
import getDefaultColumns from './defaultColumns';
import SubscriptionsProps from './SubscriptionsProps.interface';
import '../common-correspondence.scss';
import './subscriptions.scss';

const Subscriptions = ({
  gatewayEndPoint,
}: SubscriptionsProps): JSX.Element => {
  const [notification, setNotification] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [customers, setCustomers] = React.useState(null);
  const [selectedCustomer, setSelectedCustomer] = React.useState(null);
  const [selectedContacts, setSelectedContacts] = React.useState(null);
  const [customersMessage, setCustomersMessage] = React.useState(null);

  // Get the User Context
  const userContext: UserInfo = React.useContext(UserContext);
  const permissionCorrespondenceEdit: boolean = getEditPermission(userContext, PermissionGroup.Correspondence);

  const dispatch = useDispatch();

  // Make the API Call to Read the Customer Contacts
  const getCustomerContacts = (customerSummary: CustomerSummary) => async (): Promise<void> => {
    try {
      const api = new API(gatewayEndPoint);

      // Get the Customer Contacts
      const contactCollection: ContactCollection = await api.customer.getCustomerContactsV41(customerSummary.customerUuid);

      // Set the Selected Customer
      setSelectedContacts(contactCollection.contacts);

      Logging.Info('Get Contacts:', customerSummary.customerName, customerSummary.customerUuid, contactCollection.contacts);
    } catch (error) {
      const notificationData: NotificationData = {
        type: 'Error',
        title: 'Contacts Read Error',
        description: `Unable to Read Contacts for '${customerSummary.customerName}'`,
        error: error as ResponseError,
      };

      setNotification(notificationData);
    }
  };

  // Make the API Call to Update the Customer Contact
  const updateCustomerContact = (
    customerSummary: CustomerSummary,
    contact: Contact,
    emailSubscribedInd: boolean,
    contacts: Contact[],
  ) => async (): Promise<void> => {
    try {
      const api = new API(gatewayEndPoint);

      Logging.Debug('Update Contact:', customerSummary.customerName, contact.name, emailSubscribedInd, contacts);

      // Update the Contact
      await api.customer.updateCustomerContactV41(customerSummary.customerUuid, contact.uuid, emailSubscribedInd);

      // Update the Selected Contacts
      setSelectedContacts(contacts);
    } catch (error) {
      const notificationData: NotificationData = {
        type: 'Error',
        title: 'Contact Update Error',
        description: `Unable to Update Contact '${contact.name}'`,
        error: error as ResponseError,
      };

      setNotification(notificationData);
    }
  };

  const onSelectCustomer = (customer: CustomerSummary): void => {
    setSelectedCustomer(customer);

    // Valid Selected Customer
    if (customer) {
      // Load the Selected Customer Contacts
      dispatch(getCustomerContacts(customer));
    }
  };

  // Make the API Call to Add the Customer
  const getCustomers = (customerSearchText: string) => async (): Promise<void> => {
    try {
      const api = new API(gatewayEndPoint);

      const maxPageSize = 1000;
      let customFilter: string = null;
      const sortModel = ['customerName,asc'];

      if (customerSearchText) {
        const filterValue = `(customercode ct '${customerSearchText}' OR customername ct '${customerSearchText}')`;
        customFilter = `$filter=${encodeURIComponent(filterValue)}`;
      }

      // Get the Customers
      const collection: CustomerSummaryCollection = await api.customer.getCustomersV41(0, maxPageSize, null, sortModel, customFilter);

      // Get the Total Customers
      const totalCustomers: number = collection['@odata.count'] ? collection['@odata.count'] : 0;

      Logging.Debug('Customers', customerSearchText, totalCustomers);

      setCustomers(collection.value);

      // If available, get the First Customer and Select it
      const firstCustomer = totalCustomers > 0 ? collection.value[0] : null;
      onSelectCustomer(firstCustomer);

      if (maxPageSize < totalCustomers) {
        setCustomersMessage(`Only showing ${formatDecimal(maxPageSize)} of ${formatDecimal(totalCustomers)} Customers. Please use a Search filter.`);
      } else {
        setCustomersMessage(null);
      }
    } catch (error) {
      const notificationData: NotificationData = {
        type: 'Error',
        title: 'Customers Read Error',
        description: 'Unable to Read Customers',
        error: error as ResponseError,
      };

      setNotification(notificationData);
    }

    setLoading(false);
  };

  // Get the Customer Status
  const getCustomerStatus = (customer: CustomerSummary): CustomerStatus => {
    let status: CustomerStatus = customer.status || CustomerStatus.Pending;

    // If Approved Status break it down into Active or Inactive
    if (status === CustomerStatus.Approved) {
      // End Date not defined
      if (customer.endDate) {
        const currentDate = getCurrentDateOnly();
        const endDate: Date = stringToDate(customer.endDate);

        // If Today's Date is smaller than the End date the Customer is Active, otherwise it is Inactive
        status = currentDate < endDate ? CustomerStatus.Active : CustomerStatus.Inactive;
      } else {
        status = CustomerStatus.Active;
      }
    }

    return status;
  };

  // Render the Customer
  const renderCustomer = (customer: CustomerSummary): JSX.Element => {
    Logging.Debug('Render Customer', customer);

    const selected: boolean = customer.customerUuid === selectedCustomer?.customerUuid;
    const status: CustomerStatus = getCustomerStatus(customer);

    return (
      // eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions
      <div
        key={customer.customerUuid}
        className={`subscriptionsCustomer ${selected ? 'subscriptionsCustomerSelected' : ''}`}
        onClick={(event) => onSelectCustomer(customer)}
      >
        <div className="subscriptionsCustomerPrimary">
          <div className="subscriptionsCustomerPrimaryLeft">
            <div title={customer.customerName} className="subscriptionsCustomerPrimaryText">
              {customer.customerName}
            </div>
            <div title={customer.customerName} className="subscriptionsCustomerSecondaryText">
              Customer Code: {customer.customerCode}
            </div>
            {
              customer.taxpayerDefinition
                && (
                  <div
                    title={`${customer.taxpayerDefinition?.taxPayerName} / ${customer.taxpayerDefinition?.partitionName}`}
                    className="subscriptionsCustomerSecondaryText"
                  >
                    Taxpayer: {customer.taxpayerDefinition?.taxPayerName} / {customer.taxpayerDefinition?.partitionName}
                  </div>
                )
            }
          </div>
          <div className="subscriptionsCustomerPrimaryRight">
            <div className="subscriptionsCustomerStatus">
              <StatusIndicator status={status.toString()} />
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Render the Customers
  const renderCustomers = (): JSX.Element | JSX.Element[] => {
    Logging.Debug('Render Customers', customers);

    if (!customers || !customers.length) {
      return (
        <><br />No Customers found</>
      );
    }

    return customers?.map((customer: CustomerSummary) => renderCustomer(customer));
  };

  const searchCustomer = (customerSearchText: string) => {
    Logging.Info('Search Customer', customerSearchText);

    setLoading(true);

    // Get the Filtered Customers
    dispatch(getCustomers(customerSearchText));
  };

  // On Search Customer - Button
  const onSearchCustomer = (customerSearchText: string) => {
    searchCustomer(customerSearchText);
  };

  // On Search Customer - Debounce
  const onSearchChange = (customerSearchText: string) => {
    searchCustomer(customerSearchText);
  };

  // Close the Notification
  const onNotificationClose = (): void => {
    setNotification(null);
  };

  // Parse each row from the Native Data Type to the Actual Data Type
  const rowParser = (row: GridDataNativeType): GridRowData => GridRowData.fromData(row);

  const onContactSubscribeChange = (row: GridRowData): void => {
    const contactUuid: string = row.rowData.uuid;

    // Clone the Contacts
    const contacts: Contact[] = _.cloneDeep(selectedContacts);

    // Get the Modified Contact
    const contactsFiltered: Contact[] = contacts?.filter((contact: Contact) => contact.uuid === contactUuid);

    // Toggle the Subscribed State of Contact
    if (contactsFiltered && (contactsFiltered.length === 1)) {
      contactsFiltered[0].emailSubscribedInd = contactsFiltered[0].emailSubscribedInd === undefined ? true : !contactsFiltered[0].emailSubscribedInd;

      const { emailSubscribedInd } = contactsFiltered[0];

      // Update the Contacts
      dispatch(updateCustomerContact(selectedCustomer, contactsFiltered[0], emailSubscribedInd, contacts));
    } else {
      Logging.Error('Cannot find Contact:', contactUuid, selectedContacts);
    }
  };

  // Get the Columns for the Grid
  const getColumns = (): VtxColumnProps<GridRowData>[] => {
    // Get the Default Columns
    const defaultColumns: VtxColumnProps<GridRowData>[] = getDefaultColumns();

    // Column Columns with Dynamic Renderer
    const customColumns = [
      {
        dataIndex: 'subscribed',
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        render: (value: any, row: GridRowData, index: number) => (
          <ColumnSwitch
            id="subscribeSwitch"
            disabled={!permissionCorrespondenceEdit}
            checked={row.rowData.emailSubscribedInd}
            switchTextChecked="Subscribed"
            switchTextUnchecked="Unsubscribed"
            onChange={onContactSubscribeChange}
            row={row}
            align="left"
          />
        ),
      },
    ];

    // Merge the Custom Columns into the Default Columns
    return mergeDefaultAndCustomColumns(defaultColumns, customColumns);
  };

  React.useEffect(() => {
    // This Code will run once after the initial Render
    Logging.Debug('Subscriptions: Mount');

    // Get the Initial Customers
    dispatch(getCustomers(null));

    return (): void => {
      // This Code will run once the component unmounts.
      setNotification(null);
      setSelectedCustomer(null);
      setCustomers(null);
      setLoading(false);
    };
  }, []);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const debounceEventHandler = (func: any, wait: number): any => {
    const debounced = _.debounce(func, wait);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return function debounceEventHandlerFunction(event: any) {
      event.persist();
      return debounced(event?.target?.value);
    };
  };

  return (
    <>
      {
        !customers
          ? (
            <div className="correspondenceContainer">
              <div className="correspondenceHeader">
                <div className="correspondenceHeaderLeft">
                  Subscriptions
                </div>
              </div>
              <div className="correspondenceContent">
                <ContentLoading loadingMessage="Loading Customers ..." />
              </div>
            </div>
          )
          : (
            <div className="correspondenceContainer subscriptionsContainer">
              <div className="subscriptionsLeftPanel">
                <div className="correspondenceHeader">
                  Subscriptions
                </div>
                <div className="subscriptionsSearch">
                  <VtxInputSearch
                    type="search"
                    placeholder="Search by Customer Name or Code"
                    allowClear
                    loading={loading}
                    enterButton
                    onSearch={onSearchCustomer}
                    onChange={debounceEventHandler(onSearchChange, 750)}
                  />
                </div>
                <div className="subscriptionsCustomersContainer">
                  <div className="subscriptionsCustomers">
                    {renderCustomers()}
                  </div>
                </div>
                <div className="subscriptionsMessage">
                  {customersMessage}
                </div>
              </div>
              <div className="subscriptionsRightPanel">
                {
                  selectedCustomer
                    ? (
                      <>
                        <div title={`${selectedCustomer.customerName}  (${selectedCustomer.customerCode})`} className="subscriptionsCustomerName">
                          {`${selectedCustomer.customerName}  (${selectedCustomer.customerCode})`}
                        </div>
                        <div className="subscriptionsCustomerEmailSubscribers">
                          Email Subscribers
                        </div>
                        <div className="subscriptionsCustomerHorizontalLine" />
                        <div className="subscriptionsCustomerContacts">
                          <GenericGrid<GridRowData>
                            id="contactsGrid"
                            dataTypeDescription="Contacts"
                            getDefaultColumns={getColumns}
                            rowParser={rowParser}
                            model="static"
                            dataRows={selectedContacts || []}
                            verticalHeightOffset={373}
                            horizontalWidth="max-width" // Do not scale to the Width of the Content
                          />
                        </div>
                      </>
                    )
                    : (
                      <>
                        {
                          customers && customers.length > 0
                          && (
                            <div className="subscriptionsCustomerLoading">
                              <ContentLoading loadingMessage="Loading Contacts ..." />
                            </div>
                          )
                        }
                      </>
                    )
                }
              </div>
            </div>
          )
      }
      <Notification
        data={notification}
        onNotificationClose={onNotificationClose}
        autoClose
      />
    </>
  );
};

export default Subscriptions;
